import { fr, enUS, es, de, it, nl, type Locale } from 'date-fns/locale';
import {
  bookingResourceLanguage,
  normalizeBookingLanguage,
  type BookingLanguageCode,
} from '../config/i18n';
import { DUTCH_LOCALE_META, isDutchRegionalCode } from '../config/dutchLocale';

/** BCP 47 tags for Intl.DateTimeFormat / toLocaleDateString */
export const INTL_DATE_LOCALE: Record<BookingLanguageCode, string> = {
  fr: 'fr-FR',
  en: 'en-GB',
  es: 'es-ES',
  de: 'de-DE',
  it: 'it-IT',
  'nl-BE': 'nl-BE',
  'nl-NL': 'nl-NL',
};

/** date-fns locale objects (partagés pour nl-BE / nl-NL) */
export const DATE_FNS_LOCALE: Record<BookingLanguageCode, Locale> = {
  fr,
  en: enUS,
  es,
  de,
  it,
  'nl-BE': nl,
  'nl-NL': nl,
};

export function getIntlDateLocale(lang?: string | null): string {
  const code = normalizeBookingLanguage(lang);
  if (isDutchRegionalCode(code)) {
    return DUTCH_LOCALE_META[code].hreflang;
  }
  return INTL_DATE_LOCALE[code] || 'fr-FR';
}

export function getDateFnsLocale(lang?: string | null): Locale {
  return DATE_FNS_LOCALE[normalizeBookingLanguage(lang)] || fr;
}

/** dayjs language pack id (`nl`, pas `nl-BE`). */
export function getDayjsLocale(lang?: string | null): string {
  return bookingResourceLanguage(lang);
}

/** Capitalize first letter (useful for fr weekday/month in sentences). */
export function capitalizeFirstLetter(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function formatWithIntl(
  date: Date,
  options: Intl.DateTimeFormatOptions,
  lang?: string | null
): string {
  return new Intl.DateTimeFormat(getIntlDateLocale(lang), options).format(date);
}

export function formatDateShort(date: Date | string, lang?: string | null): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return typeof date === 'string' ? date : '';
  return d.toLocaleDateString(getIntlDateLocale(lang), {
    day: '2-digit',
    month: 'short',
  });
}

export function formatDateLong(date: Date | string, lang?: string | null): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return typeof date === 'string' ? date : '';
  return d.toLocaleDateString(getIntlDateLocale(lang), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
